import useInView from '@propertypal/shared/src/hooks/useInView';
import {
  MortgageCalcData,
  MortgageCalculatorFormValues,
  MortgageEvent,
} from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { getItem, setItem } from '@propertypal/shared/src/services/local.storage';
import { Property } from '@propertypal/shared/src/types';
import { generateYears } from '@propertypal/shared/src/utils/dates';
import { formatNumber, parseNumber } from '@propertypal/shared/src/utils/floatToCurrency';
import {
  getDefaultDeposit,
  getDefaultTerm,
} from '@propertypal/shared/src/utils/property/getMortgageCalculatorDefaults';
import { brokerSchema } from '@propertypal/shared/src/validation/schemas/brokerSchema';
import { useFormik } from 'formik';
import React, { CSSProperties, useEffect, useRef } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import NumberInput from '../inputs/NumberInput';
import SelectInput from '../inputs/SelectInput';
import { Text } from '../typography';
import {
  Container,
  Title,
  FormWrapper,
  FieldContainer,
  MortgagePaymentContainer,
  MortgagePayment,
  FormBox,
  ErrorText,
} from './BrokerCalculator.style.pn';

interface BrokerCalculatorProps {
  property: Property;
  mortgageCalculator: MortgageCalcData;
}

const inputStyle: CSSProperties = {
  flex: 1,
  marginRight: 15,
  marginTop: 15,
};

const BrokerCalculator: React.FC<BrokerCalculatorProps> = ({ property, mortgageCalculator }) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLElement>(null);
  const { product, loading, errorStatus, getMortgageResult, depositPercentage, logMortgageEvent } = mortgageCalculator;

  const fieldLastTouched = useRef('');
  const initialValues: MortgageCalculatorFormValues = {
    paymentTerm: getDefaultTerm(property),
    propertyValue: property.price?.price || property.price?.minPrice,
    deposit: getDefaultDeposit(property),
    interestRate: product?.interestRate || 0,
  };

  const formik = useFormik({
    initialValues: {
      paymentTerm: getItem<string>('defaultTerm') || formatNumber(getDefaultTerm(property)),
      propertyValue: formatNumber(initialValues.propertyValue),
      deposit: getItem<string>('defaultDeposit') || formatNumber(getDefaultDeposit(property)),
      interestRate: getItem<string>('defaultInterest') || formatNumber(product?.interestRate),
      fromEmail: '',
      fromTelephone: '',
      fromName: '',
    },
    validationSchema: brokerSchema,
    onSubmit: () => {},
  });

  const logEvent = async () => {
    if (product && fieldLastTouched.current) {
      const event: MortgageEvent = {
        value: parseNumber(formik.values.propertyValue),
        deposit: parseNumber(formik.values.deposit),
        term: parseInt(formik.values.paymentTerm, 10),
        fieldModified: fieldLastTouched.current,
        depositFromCookie: parseNumber(formik.values.deposit) === initialValues.deposit,
        termFromCookie: parseNumber(formik.values.paymentTerm) === initialValues.paymentTerm,
      };

      await logMortgageEvent(event);

      trackGaEvent('pdp_calc_interaction', {
        change: fieldLastTouched.current,
        page: 'PDP',
        location: 'Mortgage Calculator',
      });
    }
  };

  const fetchMortgageResult = () => {
    getMortgageResult({
      paymentTerm: parseInt(formik.values.paymentTerm, 10),
      deposit: parseNumber(formik.values.deposit),
      propertyValue: parseNumber(formik.values.propertyValue),
      interestRate: parseNumber(formik.values.interestRate),
    });
  };

  const inView = useInView(containerRef, { threshold: 0.5, rootMargin: '0px 0px 600px' }, () => {
    if (!product) fetchMortgageResult();
  });

  useInView(containerRef, { threshold: 0.5 }, () => {
    trackGaEvent('pdp_calculator_impression', {
      page: 'PDP',
      location: 'Mortgage Calculator',
    });
  });

  useEffect(() => {
    logEvent();
  }, [product]);

  useEffect(() => {
    if (inView) {
      fetchMortgageResult();
    }
  }, [formik.values.paymentTerm, formik.values.deposit, formik.values.interestRate, formik.values.propertyValue]);

  return (
    <Container data-testid="broker-calculator" ref={containerRef}>
      <form data-testid="broker-calc-form" onSubmit={formik.handleSubmit}>
        <FormBox>
          <Title>Mortgage Repayment Calculator</Title>

          <FormWrapper>
            <FieldContainer>
              <NumberInput
                containerStyle={inputStyle}
                prefix="£"
                label="Property Value"
                name="propertyValue"
                onValueChange={(value) => {
                  formik.handleChange('propertyValue')(value);
                  fieldLastTouched.current = 'Value';
                }}
                value={formik.values.propertyValue}
                error={formik.errors.propertyValue}
                testID="propertyValue"
              />

              <NumberInput
                containerStyle={inputStyle}
                prefix="£"
                label={`Deposit (${depositPercentage !== undefined ? depositPercentage : '-'}%)`}
                name="depositInput"
                onValueChange={(value) => {
                  formik.handleChange('deposit')(value);
                  setItem('defaultDeposit', value);
                  fieldLastTouched.current = 'Deposit';
                }}
                value={formik.values.deposit}
                error={formik.errors.deposit}
                testID="depositInput"
              />
            </FieldContainer>

            <FieldContainer>
              <SelectInput
                containerStyle={inputStyle}
                label="Payment Term"
                ariaLabel="Payment Term"
                id="paymentTerm"
                options={generateYears(40, true)}
                value={formik.values.paymentTerm}
                onChange={(value) => {
                  formik.handleChange('paymentTerm')(value);
                  setItem('defaultTerm', value);
                  fieldLastTouched.current = 'Term';
                }}
              />

              <NumberInput
                containerStyle={inputStyle}
                suffix="%"
                label="Interest Rate"
                name="interestInput"
                placeholder={formatNumber(product?.interestRate)}
                onValueChange={(value) => {
                  formik.handleChange('interestRate')(value);
                  setItem('defaultInterest', value);
                  fieldLastTouched.current = 'interestRate';
                }}
                value={formik.values.interestRate}
                error={formik.errors.interestRate}
                testID="interestRateInput"
                decimalScale={2}
              />
            </FieldContainer>

            <MortgagePaymentContainer>
              <MortgagePayment>
                <Text fontWeight="600" mt={15} textTransform="uppercase">
                  Monthly Payments
                </Text>

                {!loading && (
                  <Text
                    fontWeight="800"
                    fontSize={44}
                    align={!product?.monthlyPayments ? 'center' : undefined}
                    data-testid="mortgage-repayments"
                  >
                    {product?.monthlyPayments || '-'}
                  </Text>
                )}

                {!loading && !!product?.monthlyPayments && <Text fontSize={12}>Capital + Interest (inc fees)</Text>}

                {loading && (
                  <div style={{ marginTop: 25 }}>
                    <Loader color={theme.backgroundLighter} />
                  </div>
                )}
              </MortgagePayment>
            </MortgagePaymentContainer>
          </FormWrapper>

          {errorStatus?.error === 'DEPOSIT_GREATER_VALUE' && (
            <ErrorText>
              Your deposit is greater than the property value. It seems you do not require a mortgage.
            </ErrorText>
          )}

          {errorStatus?.error === 'DEPOSIT_EQUAL_VALUE' && (
            <ErrorText>
              Your deposit is the same as the property value. It seems you do not require a mortgage.
            </ErrorText>
          )}

          {errorStatus?.error === 'NO_RESULTS' && (
            <ErrorText>Based on your criteria, there are no mortgage products available at present.</ErrorText>
          )}

          {errorStatus?.error === 'INVALID_LTV' && (
            <ErrorText>
              Based on your criteria, the Loan-to-Value % for this property is less than 1%. It seems you do not require
              a mortgage.
            </ErrorText>
          )}

          {errorStatus?.error === 'SERVER_ERROR' && (
            <ErrorText>Oops, it looks like there has been an error on our server. Maybe try again?</ErrorText>
          )}

          <Text fontSize={11.5} mt={15} data-testid="small-print">
            The figures provided by this calculator are for illustrative purposes only. It does not include all of the
            details you may need to consider when calculating mortgage affordability. The accuracy and applicability in
            regards to your individual circumstances cannot be guaranteed. You are encouraged to seek personalised
            advice from qualified professionals regarding all personal finance issues.
          </Text>
        </FormBox>
      </form>
    </Container>
  );
};

export default BrokerCalculator;
